import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import { AppInfoTypeSelectField, AppInputField, AppPromptsField } from '@/form/FormUI';
import type { AddressLookupNodeType, NodeFormProps } from '@/nodes';
import { infoTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressLookupDataSchema, type AddressLookupDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof AddressLookupDataSchema>;

const AddressLookupForm: FC<NodeFormProps<AddressLookupNodeType>> = ({
    spaceId,
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, AddressLookupDataType>({
        resolver: zodResolver(AddressLookupDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: AddressLookupDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What message are we showing the user?" />

                    <AppDataKeyField
                        control={form.control}
                        name="dataKey"
                        label="Data Key"
                        placeholder="address"
                        description="The key used to store the selected address value for the rest of this guide."
                        mode="produce"
                        currentNodeId={node.id}
                    />

                    <AppInfoTypeSelectField
                        control={form.control}
                        name="infoTypeId"
                        label="Info Type"
                        description="Which info type are we saving the address as?"
                        infoTypes={infoTypes}
                        placeholder="Select Info Type"
                    />

                    <AppInputField
                        label="Button Label"
                        placeholder="Look Up"
                        control={form.control}
                        name="buttonLabel"
                        description="The label on the button to trigger the address lookup."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default AddressLookupForm;
